
import React, { useEffect, useMemo, useState } from 'react';
// fixed/updated by ChatGPT on 2026-01-25 17:20:00 – allow creating new components directly inside grid cells
import { getComponentTypes, getComponentConfig } from '../../../../config/componentTypes';
import CommonAttributesForm from '../CommonAttributesForm';
import * as SpecificForms from '../SpecificForms';
import { useAccordionState } from '../../../../hooks/useAccordionState';


// Reusable form styles (consistent with other forms in the codebase)
const formStyles = {
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '4px',
    color: '#4B5563'
  },
  input: {
    width: '100%',
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px'
  },
  select: {
    width: '100%',
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF'
  },
  colorInput: {
    width: '100%',
    height: '34px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    cursor: 'pointer'
  }
};

// Reusable FormField component
const FormField = ({ label, children, required = false, tooltip }) => (
  <div>
    <label style={formStyles.label} title={tooltip}>
      {label}
      {required && <span style={{ color: '#DC2626', marginLeft: '4px' }}>*</span>}
      {tooltip && <span style={{ marginLeft: '4px', color: '#9CA3AF', fontSize: '10px' }}>ⓘ</span>}
    </label>
    {children}
  </div>
);

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const makeCellId = (r, c) => `cell_r${r}_c${c}`;

const buildCells = (rows, cols, previousCells = []) => {
  const prevIndex = new Map();
  (previousCells || []).forEach((cell) => {
    if (cell?.row != null && cell?.col != null) {
      prevIndex.set(`${cell.row}_${cell.col}`, cell);
    }
  });

  const next = [];
  for (let r = 1; r <= rows; r += 1) {
    for (let c = 1; c <= cols; c += 1) {
      const key = `${r}_${c}`;
      const existing = prevIndex.get(key);
      next.push(
        existing
          ? { ...existing, id: existing.id || makeCellId(r, c), row: r, col: c }
          : { id: makeCellId(r, c), row: r, col: c, componentRefId: null }
      );
    }
  }
  return next;
};

// export const GridForm = ({ component, onUpdate, allComponents = [] }) => {
// fixed/updated by ChatGPT on 2026-01-25 17:20:00 – accept add handlers so grid cell can create components
export const GridForm = ({
  component,
  onUpdate,
  allComponents = [],
  onAddComponent,
  onAddFormComponent,
  onAddCardComponent,
  onDeleteComponent,
  layoutId
}) => {
  const { specificAttrs = {} } = component || {};
  const [expandedCellId, setExpandedCellId] = useState(null);
  const accordionHook = useAccordionState();

  const safeRows = clamp(Number(specificAttrs.rows || 1), 1, 6);
  const safeCols = clamp(Number(specificAttrs.cols || 1), 1, 6);

  const availableComponents = useMemo(() => {
    const list = Array.isArray(allComponents) ? allComponents : [];
    // Prevent recursion: exclude this grid itself
    return list.filter((c) => c?.id && c.id !== component.id);
  }, [allComponents, component.id]);

  // fixed/updated by ChatGPT on 2026-01-25 14:30:00 – provide component type list for grid cells
  const componentTypes = useMemo(() => getComponentTypes(), []);

  const updateSpecific = (patch) => {
    onUpdate(component.id, {
      specificAttrs: { ...specificAttrs, ...patch }
    });
  };
  const clearComponentFromAllCells = (componentId) => {
    if (!componentId) return;
    const updated = cells.map((c) =>
      c.componentRefId === componentId ? { ...c, componentRefId: null } : c
    );
    // fixed/updated by ChatGPT on 2026-01-25 18:05:00 – clear deleted component references from grid cells
    updateSpecific({ cells: updated });
  };

  // Ensure cells exist and match rows*cols (preserve existing assignments)
  useEffect(() => {
    const existingCells = Array.isArray(specificAttrs.cells) ? specificAttrs.cells : [];
    const desiredCount = safeRows * safeCols;

    const needsRebuild =
      existingCells.length !== desiredCount ||
      existingCells.some((cell) => !cell?.id || cell?.row == null || cell?.col == null);

    if (needsRebuild) {
      const rebuilt = buildCells(safeRows, safeCols, existingCells);
      // fixed/updated by ChatGPT on 2026-01-25 15:20:00 – auto-maintain cells array to match rows×cols while preserving assignments
      onUpdate(component.id, {
        specificAttrs: { ...specificAttrs, rows: safeRows, cols: safeCols, cells: rebuilt }
      });
    }
  }, [safeRows, safeCols]);

  const cells = useMemo(() => {
    const existingCells = Array.isArray(specificAttrs.cells) ? specificAttrs.cells : [];
    return existingCells.length ? existingCells : buildCells(safeRows, safeCols, []);
  }, [specificAttrs.cells, safeRows, safeCols]);

  const setRows = (value) => {
    const nextRows = clamp(Number(value || 1), 1, 6);
    const rebuilt = buildCells(nextRows, safeCols, cells);
    // fixed/updated by ChatGPT on 2026-01-25 15:20:00 – rebuild cells when rows change, preserving assignments
    onUpdate(component.id, {
      specificAttrs: { ...specificAttrs, rows: nextRows, cols: safeCols, cells: rebuilt }
    });
  };

  const setCols = (value) => {
    const nextCols = clamp(Number(value || 1), 1, 6);
    const rebuilt = buildCells(safeRows, nextCols, cells);
    // fixed/updated by ChatGPT on 2026-01-25 15:20:00 – rebuild cells when cols change, preserving assignments
    onUpdate(component.id, {
      specificAttrs: { ...specificAttrs, rows: safeRows, cols: nextCols, cells: rebuilt }
    });
  };

  const updateCellRef = (cellId, refId) => {
    const updated = cells.map((c) => (c.id === cellId ? { ...c, componentRefId: refId || null } : c));
    updateSpecific({ cells: updated });
  };

  const toggleCell = (cellId) => setExpandedCellId(expandedCellId === cellId ? null : cellId);

  // fixed/updated by ChatGPT on 2026-01-25 17:20:00 – helper: mark component as nested so it won’t render outside grid
  const markAsNested = (newComponentId) => {
    if (!newComponentId) return;
    try {
      onUpdate(newComponentId, {
        // these fields will be used by PreviewRenderer to ignore nested components in slot rendering
        parentComponentId: component.id,
        isNested: true
      });
    } catch (e) {
      console.warn('[GridForm] Failed to mark component nested', e);
    }
  };

  // fixed/updated by ChatGPT on 2026-01-25 17:20:00 – create component and auto-assign to cell
  const createAndAssignToCell = (cellId, type) => {
  if (!cellId || !type) return;
    
  // Expand this cell automatically so user sees it
  setExpandedCellId(cellId);

  // If parent didn’t pass add handlers, fail gracefully
  if (!onAddComponent && !onAddFormComponent && !onAddCardComponent) {
    console.warn('[GridForm] Missing add handlers. Ensure Accordion/ComponentsList forwards onAddComponent props.');
    return;
  }

  // Special types
  if (type === 'form') {
    onAddFormComponent({
      onCreated: (newId) => {
        updateCellRef(cellId, newId);
        onUpdate(newId, {
          isNested: true,
          parentComponentId: component.id,
          slotId: null
        });

        // fixed/updated by ChatGPT on 2026-01-26 19:10:00 – keep the same grid cell expanded after creation
        setExpandedCellId(cellId);
      }
    });

    // keep grid cell expanded
    setExpandedCellId(cellId);
    return;
  }

  if (type === 'card') {
    onAddCardComponent?.(component.slotId || null, {
      onCreated: (newId) => {
        updateCellRef(cellId, newId);

        // mark nested + remove slot so it won’t render outside the grid
        onUpdate(newId, {
          isNested: true,
          parentComponentId: component.id,
          slotId: null
        });

        // fixed/updated by ChatGPT on 2026-01-26 19:10:00 – keep the same grid cell expanded after creation
        setExpandedCellId(cellId);
      }
    });

    setExpandedCellId(cellId);
    return;
  }

  // Regular add
  // const newId = onAddComponent?.(type, null, '__nested__');

  // fixed/updated by ChatGPT on 2026-01-26 19:10:00 – create nested component without expanding its own accordion; keep Grid expanded
  const newId = onAddComponent?.(type, null, '__nested__');
  accordionHook.expandAccordion(component.id);
  if (newId) {
    updateCellRef(cellId, newId);
    onUpdate(newId, {
      // these fields will be used by PreviewRenderer to ignore nested components in slot rendering
      parentComponentId: component.id,
      isNested: true,
      slotId: null // fixed/updated by ChatGPT on 2026-01-26 19:10:00 – ensure nested child never renders in a page slot
    });
  }

  setExpandedCellId(cellId);
  return;
};

  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      {/* Grid Settings */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
        <FormField label="Grid Height (Rows)" required tooltip="How many horizontal lines of boxes">
          <select value={safeRows} onChange={(e) => setRows(e.target.value)} style={formStyles.select}>
            {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </FormField>

        <FormField label="Grid Width (Columns)" required tooltip="How many boxes in one row">
          <select value={safeCols} onChange={(e) => setCols(e.target.value)} style={formStyles.select}>
            {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </FormField>

        <FormField label="Gap between boxes (px)" tooltip="Space between grid boxes">
          <input
            type="number"
            min={0}
            max={60}
            value={specificAttrs.gap ?? 12}
            onChange={(e) => updateSpecific({ gap: Number(e.target.value) })}
            style={formStyles.input}
          />
        </FormField>

        <FormField label="Outer Padding (px)" tooltip="Space around the whole grid">
          <input
            type="number"
            min={0}
            max={80}
            value={specificAttrs.padding ?? 12}
            onChange={(e) => updateSpecific({ padding: Number(e.target.value) })}
            style={formStyles.input}
          />
        </FormField>

        <FormField label="Background">
          <input
            type="color"
            value={specificAttrs.backgroundColor || '#ffffff'}
            onChange={(e) => updateSpecific({ backgroundColor: e.target.value })}
            style={formStyles.colorInput}
          />
        </FormField>

        <FormField label={`Corner Roundness (${specificAttrs.borderRadius || 0}px)`}>
          <input
            type="range"
            value={specificAttrs.borderRadius || 0}
            onChange={(e) => updateSpecific({ borderRadius: Number(e.target.value) })}
            min="0"
            max="32"
            style={{ width: '100%', marginTop: '8px' }}
          />
        </FormField>

        <FormField label="Shadow">
          <select
            value={specificAttrs.shadow || 'none'}
            onChange={(e) => updateSpecific({ shadow: e.target.value })}
            style={formStyles.select}
          >
            <option value="none">None</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </FormField>

        <FormField label="Show Cell Borders (Builder)">
          <select
            value={String(specificAttrs.showCellBorders ?? true)}
            onChange={(e) => updateSpecific({ showCellBorders: e.target.value === 'true' })}
            style={formStyles.select}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </FormField>
      </div>

      {/* Cells */}
      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
          Grid Cells ({safeRows}×{safeCols} = {safeRows * safeCols})
        </div>

        <div style={{ display: 'grid', gap: '10px' }}>
          {cells.map((cell) => {
            const isExpanded = expandedCellId === cell.id;
            const label = `Row ${cell.row} • Col ${cell.col}`;
            const selectedComponent = (allComponents || []).find(c => c?.id === cell.componentRefId);
            const selectedConfig = selectedComponent ? getComponentConfig(selectedComponent.type) : null;
            const SelectedSpecificForm = selectedConfig ? SpecificForms[selectedConfig.formComponent] : null;
            return (
              <div
                key={cell.id}
                style={{
                  padding: '10px',
                  backgroundColor: isExpanded ? '#FFFFFF' : '#F9FAFB',
                  border: isExpanded ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                  borderRadius: '8px',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <button
                      type="button"
                      onClick={() => toggleCell(cell.id)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '10px',
                        backgroundColor: 'transparent',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        color: '#6B7280'
                      }}
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </button>

                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>
                        {label}
                      </div>
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px' }}>
                        Choose what to show inside this box
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      // fixed/updated by ChatGPT on 2026-01-25 18:05:00 – clear only this cell reference (keeps component reusable)
                      clearComponentFromAllCells(cell.componentRefId);
                      onDeleteComponent?.(cell.componentRefId);
                      updateCellRef(cell.id, null);
                    }}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      background: '#FFFFFF',
                      cursor: 'pointer',
                      color: '#374151',
                      flex: 1
                    }}
                  >
                    ✖ Clear Cell
                  </button>

                  {/* <button
                    type="button"
                    disabled={!cell.componentRefId}
                    onClick={() => {
                      if (!cell.componentRefId) return;

                      const ok = window.confirm(
                        'Delete this component completely?\n\n' +
                        'This will remove it from the page and also clear it from any grid cell using it.'
                      );
                      if (!ok) return;

                      clearComponentFromAllCells(cell.componentRefId);

                      // fixed/updated by ChatGPT on 2026-01-25 18:05:00 – request parent to delete component instance
                      onDeleteComponent?.(cell.componentRefId);

                      // clear this cell ref immediately
                      updateCellRef(cell.id, null);
                    }}
                    style={{
                      padding: '6px 10px',
                      fontSize: '12px',
                      border: '1px solid #FCA5A5',
                      borderRadius: '6px',
                      background: cell.componentRefId ? '#FEE2E2' : '#F9FAFB',
                      cursor: cell.componentRefId ? 'pointer' : 'not-allowed',
                      color: '#DC2626',
                      flex: 1
                    }}
                  >
                    🗑 Delete Component
                  </button> */}
                </div>

                <FormField label="Component inside this cell" tooltip="Pick an existing component or create a new one for this cell">
                  <select
                    value={`__create__:${cell.componentRefId?.split('_')[0]}` || ''}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      const v = e.target.value;

                      // Create flow encoded in same dropdown
                      if (v && v.startsWith('__create__:')) {
                        const type = v.replace('__create__:', '');
                        if(cell.componentRefId) {
                          // Clear existing component from this cell before creating new one
                          clearComponentFromAllCells(cell.componentRefId);
                          onDeleteComponent?.(cell.componentRefId);
                          updateCellRef(cell.id, null);
                        }
                        createAndAssignToCell(cell.id, type);
                        // fixed/updated by ChatGPT on 2026-01-26 18:25:00 – create nested component and bind directly to cell
                        // const newId = onAddComponent?.(type, null, '__nested__');

                        // if (newId) {
                        //   updateCellRef(cell.id, newId);

                          // fixed/updated by ChatGPT on 2026-01-26 18:25:00 – mark parent relationship
                        //   onUpdate(newId, { parentComponentId: component.id, isNested: true });
                        // }
                        return;
                      }

                      // Existing component selected
                      updateCellRef(cell.id, v || null);
                    }}
                    style={formStyles.select}
                  >
                    <option value="">(Empty)</option>

                    {/* <optgroup label="Existing Components">
                      {availableComponents
                        .filter(c => !c?.isNested && !c?.parentComponentId) // only top-level reusable ones
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.type} • {c.name || c.id}
                          </option>
                        ))}
                    </optgroup> */}

                    <optgroup label="Select component">
                      {componentTypes
                        .filter(t => t?.value && t.value !== 'grid') // avoid infinite nesting
                        .map(t => (
                          <option key={t.value} value={`__create__:${t.value}`}>
                          {t.label}
                          </option>
                        ))}
                    </optgroup>
                  </select>
                </FormField>

                {selectedComponent && (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: '#374151', marginBottom: '8px' }}>
                      Edit: {selectedConfig?.label || selectedComponent.type}
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '6px' }}>
                        Common Attributes
                      </div>
                      <CommonAttributesForm component={selectedComponent} onUpdate={onUpdate} />
                    </div>

                    {SelectedSpecificForm && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '6px' }}>
                          {selectedConfig?.label || selectedComponent.type} Attributes
                        </div>
                        <SelectedSpecificForm
                          component={selectedComponent}
                          onUpdate={onUpdate}
                          layoutId={layoutId}
                          allComponents={allComponents}
                        />
                      </div>
                    )}
                  </div>
                )}


                {isExpanded && (
                  <div style={{
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: '1px solid #E5E7EB',
                    fontSize: '11px',
                    color: '#6B7280'
                  }}>
                    ✅ This cell will display the selected component inside its grid box.
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {availableComponents.length === 0 && (
          <p style={{ fontSize: '11px', color: '#9CA3AF', textAlign: 'center', margin: '12px 0' }}>
            No existing components found yet. Use “Create a NEW component for this cell” above.
          </p>
        )}
      </div>
    </div>
  );
};

